import { CustomGroupCard } from "@/components/custom-group-card";
import { LanguageVariantsSection } from "@/components/language-variants-section";
import { MoodFeelingCard } from "@/components/mood-feeling-card";
import { SystemGroupCard } from "@/components/system-group-card-props";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import {
  TagGroup,
  TagGroupType,
  TagGroupVariant,
  TagOption,
  TagOptionVariant,
  TaxonomyStatus,
} from "@/lib/schema";
import { SUPPORTED_LANGUAGES } from "@/lib/studio-constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  Brain,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";

const STATUS_CONFIG: Record<
  TaxonomyStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  active: { label: "Active", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
};

export default function TagGroupsManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TagGroup | null>(null);
  const [editingOption, setEditingOption] = useState<TagOption | null>(null);
  const [targetGroupId, setTargetGroupId] = useState<string>("");
  const [groupName, setGroupName] = useState("");
  const [groupType, setGroupType] = useState<TagGroupType>("feeling");
  const [groupDescription, setGroupDescription] = useState("");
  const [groupApprovalRequired, setGroupApprovalRequired] = useState(true);
  const [groupPrimaryLanguageCode, setGroupPrimaryLanguageCode] = useState("");
  const [optionLabel, setOptionLabel] = useState("");
  const [optionDescription, setOptionDescription] = useState("");
  const [optionDisplayOrder, setOptionDisplayOrder] = useState(0);
  const [optionPrimaryLanguageCode, setOptionPrimaryLanguageCode] =
    useState("");
  const [optionParentTagOptionIds, setOptionParentTagOptionIds] = useState<
    string[]
  >([]);

  const [groupVariants, setGroupVariants] = useState<TagGroupVariant[]>([]);
  const [activeGroupVariantIndex, setActiveGroupVariantIndex] = useState(0);
  const [pendingGroupRemoveIndex, setPendingGroupRemoveIndex] = useState<
    number | null
  >(null);

  const [optionVariants, setOptionVariants] = useState<TagOptionVariant[]>([]);
  const [activeOptionVariantIndex, setActiveOptionVariantIndex] = useState(0);
  const [pendingOptionRemoveIndex, setPendingOptionRemoveIndex] = useState<
    number | null
  >(null);

  const isAdmin =
    user?.roles.includes("super_admin") ||
    user?.roles.includes("admin") ||
    false;

  // Same keys the content forms read, so archive/restore here refreshes them.
  const TAG_GROUPS_QUERY_KEY = ["/content/tag-groups"];
  const TAG_OPTIONS_QUERY_KEY = ["/content/tag-options"];

  const {
    data: tagGroups = [],
    isLoading: loadingGroups,
    isError: tagGroupsError,
    error: tagGroupsErrorDetail,
    refetch: refetchTagGroups,
  } = useQuery<TagGroup[]>({
    queryKey: TAG_GROUPS_QUERY_KEY,
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const {
    data: tagOptions = [],
    isLoading: loadingOptions,
    isError: tagOptionsError,
    error: tagOptionsErrorDetail,
    refetch: refetchTagOptions,
  } = useQuery<TagOption[]>({
    queryKey: TAG_OPTIONS_QUERY_KEY,
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const moodGroup = useMemo(
    () => tagGroups.find((g) => g.type === "mood"),
    [tagGroups],
  );
  const feelingGroup = useMemo(
    () => tagGroups.find((g) => g.type === "feeling"),
    [tagGroups],
  );
  const moodOptions = useMemo(
    () =>
      moodGroup
        ? tagOptions
            .filter((o) => o.groupId === moodGroup.id)
            .sort((a, b) => a.displayOrder - b.displayOrder)
        : [],
    [tagOptions, moodGroup],
  );
  const feelingOptions = useMemo(
    () =>
      feelingGroup
        ? tagOptions
            .filter((o) => o.groupId === feelingGroup.id)
            .sort((a, b) => a.displayOrder - b.displayOrder)
        : [],
    [tagOptions, feelingGroup],
  );
  const feelingsByMood = useMemo(() => {
    const map = new Map<string, TagOption[]>();
    for (const mood of moodOptions) {
      map.set(
        mood.id,
        feelingOptions.filter((f) => f.parentTagOptionIds?.includes(mood.id)),
      );
    }
    return map;
  }, [moodOptions, feelingOptions]);

  const { data: fetchedGroupVariants } = useQuery<TagGroupVariant[]>({
    queryKey: [...TAG_GROUPS_QUERY_KEY, editingGroup?.id ?? "", "variants"],
    queryFn: () =>
      editingGroup
        ? ContentTagService.getTagGroupVariants(editingGroup.id)
        : Promise.resolve([]),
    enabled: !!editingGroup?.id && groupDialogOpen,
  });

  const { data: fetchedOptionVariants } = useQuery<TagOptionVariant[]>({
    queryKey: [...TAG_OPTIONS_QUERY_KEY, editingOption?.id ?? "", "variants"],
    queryFn: () =>
      editingOption
        ? ContentTagService.getTagOptionVariants(editingOption.id)
        : Promise.resolve([]),
    enabled: !!editingOption?.id && optionDialogOpen,
  });

  useEffect(() => {
    if (tagGroupsError && tagGroupsErrorDetail) {
      toast({
        title: "Failed to load tag groups",
        description: tagGroupsErrorDetail.message,
        variant: "destructive",
      });
    }
  }, [tagGroupsError, tagGroupsErrorDetail, toast]);

  useEffect(() => {
    if (tagOptionsError && tagOptionsErrorDetail) {
      toast({
        title: "Failed to load tag options",
        description: tagOptionsErrorDetail.message,
        variant: "destructive",
      });
    }
  }, [tagOptionsError, tagOptionsErrorDetail, toast]);

  useEffect(() => {
    if (fetchedGroupVariants && editingGroup) {
      setGroupVariants(fetchedGroupVariants);
      setActiveGroupVariantIndex(0);
    }
  }, [fetchedGroupVariants, editingGroup]);

  useEffect(() => {
    if (fetchedOptionVariants && editingOption) {
      setOptionVariants(fetchedOptionVariants);
      setActiveOptionVariantIndex(0);
    }
  }, [fetchedOptionVariants, editingOption]);

  const availableGroupLanguages = useMemo(
    () =>
      SUPPORTED_LANGUAGES.filter(
        (lang) => !groupVariants.some((v) => v.languageCode === lang.code),
      ),
    [groupVariants],
  );

  const availableOptionLanguages = useMemo(
    () =>
      SUPPORTED_LANGUAGES.filter(
        (lang) => !optionVariants.some((v) => v.languageCode === lang.code),
      ),
    [optionVariants],
  );

  const saveGroupVariantsMutation = useMutation({
    mutationFn: async (groupId: string) => {
      for (const variant of groupVariants) {
        if (variant.id) {
          await ContentTagService.updateTagGroupVariant(variant.id, {
            localizedTitle: variant.localizedTitle,
            localizedDescription: variant.localizedDescription,
            languageCode: variant.languageCode,
            isDefault: variant.isDefault,
          });
        } else {
          await ContentTagService.createTagGroupVariant(groupId, {
            localizedTitle: variant.localizedTitle,
            localizedDescription: variant.localizedDescription,
            languageCode: variant.languageCode,
            isDefault: variant.isDefault,
          });
        }
      }
      const existingIds = (fetchedGroupVariants || [])
        .map((v) => v.id)
        .filter(Boolean);
      const currentIds = groupVariants.map((v) => v.id).filter(Boolean);
      for (const oldId of existingIds) {
        if (oldId && !currentIds.includes(oldId)) {
          await ContentTagService.deleteTagGroupVariant(oldId);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAG_GROUPS_QUERY_KEY });
      if (editingGroup) {
        queryClient.invalidateQueries({
          queryKey: [...TAG_GROUPS_QUERY_KEY, editingGroup.id, "variants"],
        });
      }
    },
  });

  const saveOptionVariantsMutation = useMutation({
    mutationFn: async (optionId: string) => {
      for (const variant of optionVariants) {
        if (variant.id) {
          await ContentTagService.updateTagOptionVariant(variant.id, {
            localizedLabel: variant.localizedLabel,
            localizedDescription: variant.localizedDescription,
            languageCode: variant.languageCode,
            isDefault: variant.isDefault,
          });
        } else {
          await ContentTagService.createTagOptionVariant(optionId, {
            localizedLabel: variant.localizedLabel,
            localizedDescription: variant.localizedDescription,
            languageCode: variant.languageCode,
            isDefault: variant.isDefault,
          });
        }
      }
      const existingIds = (fetchedOptionVariants || [])
        .map((v) => v.id)
        .filter(Boolean);
      const currentIds = optionVariants.map((v) => v.id).filter(Boolean);
      for (const oldId of existingIds) {
        if (oldId && !currentIds.includes(oldId)) {
          await ContentTagService.deleteTagOptionVariant(oldId);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAG_OPTIONS_QUERY_KEY });
      if (editingOption) {
        queryClient.invalidateQueries({
          queryKey: [...TAG_OPTIONS_QUERY_KEY, editingOption.id, "variants"],
        });
      }
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: async () => {
      return ContentTagService.createTagGroup({
        name: groupName,
        type: groupType.trim() || "custom",
        description: groupDescription || null,
        approvalRequired: groupApprovalRequired,
        primaryLanguageCode: groupPrimaryLanguageCode || "en",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to create tag group",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<TagGroup>;
    }) => {
      return ContentTagService.updateTagGroup(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAG_GROUPS_QUERY_KEY });
      toast({ title: "Tag group updated" });
      closeGroupDialog();
    },
    onError: (error) => {
      toast({
        title: "Failed to update tag group",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const groupStatusMutation = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: TaxonomyStatus;
    }) => {
      const patch: Partial<TagGroup> = { status };
      if (status === "archived")
        (patch as Record<string, unknown>).archivedAt =
          new Date().toISOString();
      if (status === "active")
        (patch as Record<string, unknown>).archivedAt = null;
      return ContentTagService.updateTagGroup(id, patch);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TAG_GROUPS_QUERY_KEY });
      const label = STATUS_CONFIG[variables.status]?.label || variables.status;
      toast({ title: `Tag group moved to ${label}` });
    },
    onError: (error) => {
      toast({
        title: "Failed to update status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const targetGroupType = useMemo(() => {
    const group = tagGroups.find((g) => g.id === targetGroupId);
    return group?.type;
  }, [tagGroups, targetGroupId]);

  const isFeelingGroup = targetGroupType === "feeling";

  const createOptionMutation = useMutation({
    mutationFn: async () => {
      const body = {
        groupId: targetGroupId,
        label: optionLabel,
        description: optionDescription || null,
        displayOrder: optionDisplayOrder,
        primaryLanguageCode: optionPrimaryLanguageCode || "en",
        ...(isFeelingGroup && { parentTagOptionIds: optionParentTagOptionIds }),
      };
      return ContentTagService.createTagOption(body);
    },
    onError: (error) => {
      toast({
        title: "Failed to create tag option",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateOptionMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<TagOption>;
    }) => {
      return ContentTagService.updateTagOption(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAG_OPTIONS_QUERY_KEY });
      toast({ title: "Tag option updated" });
      closeOptionDialog();
    },
    onError: (error) => {
      toast({
        title: "Failed to update tag option",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const optionStatusMutation = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: TaxonomyStatus;
    }) => {
      const patch: Partial<TagOption> = { status };
      if (status === "archived")
        (patch as Record<string, unknown>).archivedAt =
          new Date().toISOString();
      if (status === "active")
        (patch as Record<string, unknown>).archivedAt = null;
      return ContentTagService.updateTagOption(id, patch);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TAG_OPTIONS_QUERY_KEY });
      const label = STATUS_CONFIG[variables.status]?.label || variables.status;
      toast({ title: `Tag option moved to ${label}` });
    },
    onError: (error) => {
      toast({
        title: "Failed to update status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const reorderOptionMutation = useMutation({
    mutationFn: async ({ id, newOrder }: { id: string; newOrder: number }) => {
      return ContentTagService.updateTagOption(id, { displayOrder: newOrder });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAG_OPTIONS_QUERY_KEY });
    },
    onError: (error) => {
      toast({
        title: "Failed to reorder",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupName("");
    setGroupType("custom");
    setGroupDescription("");
    setGroupApprovalRequired(true);
    setGroupPrimaryLanguageCode("en");
    setGroupVariants([
      {
        languageCode: "en",
        isDefault: true,
        localizedTitle: "",
        localizedDescription: "",
      },
    ]);
    setActiveGroupVariantIndex(0);
    setPendingGroupRemoveIndex(null);
    setGroupDialogOpen(true);
  };

  const openEditGroup = (group: TagGroup) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupType(group.type);
    setGroupDescription(group.description || "");
    setGroupApprovalRequired(group.approvalRequired);
    setGroupPrimaryLanguageCode(group.primaryLanguageCode || "en");
    setGroupVariants([]);
    setActiveGroupVariantIndex(0);
    setPendingGroupRemoveIndex(null);
    setGroupDialogOpen(true);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupVariants([]);
    setActiveGroupVariantIndex(0);
    setPendingGroupRemoveIndex(null);
  };

  const openCreateOption = (groupId: string, presetParentMoodId?: string) => {
    setEditingOption(null);
    setTargetGroupId(groupId);
    setOptionLabel("");
    setOptionDescription("");
    setOptionDisplayOrder(getGroupOptions(groupId).length);
    setOptionParentTagOptionIds(presetParentMoodId ? [presetParentMoodId] : []);
    setOptionPrimaryLanguageCode("en");
    setOptionVariants([
      {
        languageCode: "en",
        isDefault: true,
        localizedLabel: "",
        localizedDescription: "",
      },
    ]);
    setActiveOptionVariantIndex(0);
    setPendingOptionRemoveIndex(null);
    setOptionDialogOpen(true);
  };

  const openEditOption = (option: TagOption) => {
    setEditingOption(option);
    setTargetGroupId(option.groupId);
    setOptionLabel(option.label);
    setOptionDescription(option.description || "");
    setOptionDisplayOrder(option.displayOrder);
    setOptionParentTagOptionIds(option.parentTagOptionIds || []);
    setOptionPrimaryLanguageCode(option.primaryLanguageCode || "en");
    setOptionVariants([]);
    setActiveOptionVariantIndex(0);
    setPendingOptionRemoveIndex(null);
    setOptionDialogOpen(true);
  };

  const closeOptionDialog = () => {
    setOptionDialogOpen(false);
    setEditingOption(null);
    setOptionParentTagOptionIds([]);
    setOptionVariants([]);
    setActiveOptionVariantIndex(0);
    setPendingOptionRemoveIndex(null);
  };

  const addGroupVariant = (langCode: string) => {
    if (groupVariants.some((v) => v.languageCode === langCode)) return;
    const isFirst = groupVariants.length === 0;
    setGroupVariants((prev) => [
      ...prev,
      {
        languageCode: langCode,
        isDefault: isFirst,
        localizedTitle: "",
        localizedDescription: "",
      },
    ]);
    setActiveGroupVariantIndex(groupVariants.length);
  };

  const confirmRemoveGroupVariant = () => {
    if (pendingGroupRemoveIndex === null) return;
    if (groupVariants[pendingGroupRemoveIndex]?.isDefault) return;
    const updated = groupVariants.filter(
      (_, i) => i !== pendingGroupRemoveIndex,
    );
    setGroupVariants(updated);
    if (activeGroupVariantIndex >= updated.length) {
      setActiveGroupVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingGroupRemoveIndex(null);
  };

  const setGroupAsDefault = (index: number) => {
    if (index < 0 || index >= groupVariants.length) return;
    const updated = groupVariants.map((v, i) => ({
      ...v,
      isDefault: i === index,
    }));
    setGroupVariants(updated);
    setActiveGroupVariantIndex(index);
  };

  const updateGroupVariantField = (
    field: keyof TagGroupVariant,
    value: string,
  ) => {
    const updated = [...groupVariants];
    updated[activeGroupVariantIndex] = {
      ...updated[activeGroupVariantIndex],
      [field]: value,
    };
    setGroupVariants(updated);
  };

  const addOptionVariant = (langCode: string) => {
    if (optionVariants.some((v) => v.languageCode === langCode)) return;
    const isFirst = optionVariants.length === 0;
    setOptionVariants((prev) => [
      ...prev,
      {
        languageCode: langCode,
        isDefault: isFirst,
        localizedLabel: "",
        localizedDescription: "",
      },
    ]);
    setActiveOptionVariantIndex(optionVariants.length);
  };

  const confirmRemoveOptionVariant = () => {
    if (pendingOptionRemoveIndex === null) return;
    if (optionVariants[pendingOptionRemoveIndex]?.isDefault) return;
    const updated = optionVariants.filter(
      (_, i) => i !== pendingOptionRemoveIndex,
    );
    setOptionVariants(updated);
    if (activeOptionVariantIndex >= updated.length) {
      setActiveOptionVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingOptionRemoveIndex(null);
  };

  const setOptionAsDefault = (index: number) => {
    if (index < 0 || index >= optionVariants.length) return;
    const updated = optionVariants.map((v, i) => ({
      ...v,
      isDefault: i === index,
    }));
    setOptionVariants(updated);
    setActiveOptionVariantIndex(index);
  };

  const updateOptionVariantField = (
    field: keyof TagOptionVariant,
    value: string,
  ) => {
    const updated = [...optionVariants];
    updated[activeOptionVariantIndex] = {
      ...updated[activeOptionVariantIndex],
      [field]: value,
    };
    setOptionVariants(updated);
  };

  const handleSaveGroup = () => {
    if (!groupName.trim()) return;
    if (editingGroup) {
      updateGroupMutation.mutate(
        {
          id: editingGroup.id,
          data: {
            name: groupName,
            type: groupType,
            description: groupDescription || null,
            approvalRequired: groupApprovalRequired,
            primaryLanguageCode: groupPrimaryLanguageCode || "en",
          },
        },
        {
          onSuccess: () => {
            if (groupVariants.length > 0) {
              saveGroupVariantsMutation.mutate(editingGroup.id, {
                onError: (error) => {
                  toast({
                    title: "Failed to save tag group variants",
                    description: error.message,
                    variant: "destructive",
                  });
                },
              });
            }
          },
        },
      );
    } else {
      createGroupMutation.mutate(undefined, {
        onSuccess: (created) => {
          if (groupVariants.length > 0 && created?.id) {
            saveGroupVariantsMutation.mutate(created.id, {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: TAG_GROUPS_QUERY_KEY,
                });
                toast({ title: "Tag group created" });
                closeGroupDialog();
              },
              onError: (error) => {
                queryClient.invalidateQueries({
                  queryKey: TAG_GROUPS_QUERY_KEY,
                });
                toast({
                  title:
                    "Tag group created but failed to save language variants",
                  description: error.message,
                  variant: "destructive",
                });
              },
            });
          } else {
            queryClient.invalidateQueries({
              queryKey: TAG_GROUPS_QUERY_KEY,
            });
            toast({ title: "Tag group created" });
            closeGroupDialog();
          }
        },
      });
    }
  };

  const handleSaveOption = () => {
    if (!optionLabel.trim()) return;
    if (editingOption) {
      const data: Partial<TagOption> = {
        label: optionLabel,
        description: optionDescription || null,
        displayOrder: optionDisplayOrder,
        primaryLanguageCode: optionPrimaryLanguageCode || "en",
      };
      if (isFeelingGroup) {
        data.parentTagOptionIds = optionParentTagOptionIds;
      }
      updateOptionMutation.mutate(
        {
          id: editingOption.id,
          data,
        },
        {
          onSuccess: () => {
            if (optionVariants.length > 0) {
              saveOptionVariantsMutation.mutate(editingOption.id, {
                onError: (error) => {
                  toast({
                    title: "Failed to save tag option variants",
                    description: error.message,
                    variant: "destructive",
                  });
                },
              });
            }
          },
        },
      );
    } else {
      createOptionMutation.mutate(undefined, {
        onSuccess: (created) => {
          if (optionVariants.length > 0 && created?.id) {
            saveOptionVariantsMutation.mutate(created.id, {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: TAG_OPTIONS_QUERY_KEY,
                });
                toast({ title: "Tag option created" });
                closeOptionDialog();
              },
              onError: (error) => {
                queryClient.invalidateQueries({
                  queryKey: TAG_OPTIONS_QUERY_KEY,
                });
                toast({
                  title:
                    "Tag option created but failed to save language variants",
                  description: error.message,
                  variant: "destructive",
                });
              },
            });
          } else {
            queryClient.invalidateQueries({
              queryKey: TAG_OPTIONS_QUERY_KEY,
            });
            toast({ title: "Tag option created" });
            closeOptionDialog();
          }
        },
      });
    }
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions
      .filter((o) => o.groupId === groupId)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  const handleReorderOption = (opt: TagOption, direction: "up" | "down") => {
    const siblings = getGroupOptions(opt.groupId);
    const idx = siblings.findIndex((o) => o.id === opt.id);
    if (direction === "up" && idx > 0) {
      reorderOptionMutation.mutate({
        id: opt.id,
        newOrder: siblings[idx - 1].displayOrder,
      });
      reorderOptionMutation.mutate({
        id: siblings[idx - 1].id,
        newOrder: opt.displayOrder,
      });
    } else if (direction === "down" && idx < siblings.length - 1) {
      reorderOptionMutation.mutate({
        id: opt.id,
        newOrder: siblings[idx + 1].displayOrder,
      });
      reorderOptionMutation.mutate({
        id: siblings[idx + 1].id,
        newOrder: opt.displayOrder,
      });
    }
  };

  const SYSTEM_PROTECTED_GROUP_IDS = [
    "tg-mood-type",
    "tg-feeling-type",
    "tg-cycle-phase",
    "tg-concentration-label",
    "tg-content-type-label",
    "tg-shift-label",
  ];

  const hasMoodFeelingSection = !!(moodGroup && feelingGroup);
  const systemGroups = tagGroups.filter((g) => {
    if (hasMoodFeelingSection && (g.type === "mood" || g.type === "feeling"))
      return false;
    if (!SYSTEM_PROTECTED_GROUP_IDS.includes(g.id)) return false;
    return (
      !searchQuery || g.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });
  const filteredGroups = tagGroups.filter((g) => {
    if (hasMoodFeelingSection && (g.type === "mood" || g.type === "feeling"))
      return false;
    if (SYSTEM_PROTECTED_GROUP_IDS.includes(g.id)) return false;
    return (
      !searchQuery || g.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });
  const showMoodFeelingSection =
    hasMoodFeelingSection &&
    (!searchQuery ||
      "mood".includes(searchQuery.toLowerCase()) ||
      "feeling".includes(searchQuery.toLowerCase()) ||
      "mood & feelings".includes(searchQuery.toLowerCase()));

  return (
    <div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      data-testid="tag-intelligence-page"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio">
          <Button variant="ghost" size="icon" data-testid="button-back-studio">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-tag-groups-title"
          >
            Tag Groups
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Structured tag system for mood, feelings, shifts, and content
            tagging
          </p>
        </div>
        {/* <Button onClick={openCreateGroup} data-testid="button-create-group">
          <Plus className="h-4 w-4 mr-2" />
          Create Tag Group
        </Button> */}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tag groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search"
            />
          </div>
        </CardContent>
      </Card>

      {tagGroupsError || tagOptionsError ? (
        <Card>
          <CardContent className="py-16">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="p-4 rounded-full bg-destructive/10 mb-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">
                Failed to load tag data
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                {tagGroupsErrorDetail?.message ||
                  tagOptionsErrorDetail?.message}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  refetchTagGroups();
                  refetchTagOptions();
                }}
                data-testid="button-retry-tag-data"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : loadingGroups || loadingOptions ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !showMoodFeelingSection &&
        systemGroups.length === 0 &&
        filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="p-4 rounded-full bg-muted mb-4">
                <Brain className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">
                No tag groups yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create tag groups like Feeling Type, Mood Type, or Shift Labels
              </p>
              <Button
                onClick={openCreateGroup}
                data-testid="button-create-first-group"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Tag Group
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {showMoodFeelingSection && (
            <MoodFeelingCard
              moodOptions={moodOptions}
              feelingOptions={feelingOptions}
              feelingsByMood={feelingsByMood}
              feelingGroup={feelingGroup}
              onEditOption={openEditOption}
              onCreateOption={openCreateOption}
              onArchiveFeeling={(id) =>
                optionStatusMutation.mutate({ id, status: "archived" })
              }
              onRestoreFeeling={(id) =>
                optionStatusMutation.mutate({ id, status: "active" })
              }
              isAdmin={isAdmin}
            />
          )}

          {systemGroups
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((group) => {
              const options = getGroupOptions(group.id);
              return (
                <SystemGroupCard
                  key={group.id}
                  group={group}
                  options={options}
                  onEditOption={openEditOption}
                  onCreateOption={() => openCreateOption(group.id)}
                  onArchiveOption={(id) =>
                    optionStatusMutation.mutate({ id, status: "archived" })
                  }
                  onRestoreOption={(id) =>
                    optionStatusMutation.mutate({ id, status: "active" })
                  }
                  isAdmin={isAdmin}
                />
              );
            })}

          {filteredGroups
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((group) => {
              const options = getGroupOptions(group.id);
              return (
                <CustomGroupCard
                  key={group.id}
                  group={group}
                  options={options}
                  onEditGroup={openEditGroup}
                  onEditOption={openEditOption}
                  onCreateOption={() => openCreateOption(group.id)}
                  onArchiveGroup={(id) =>
                    groupStatusMutation.mutate({ id, status: "archived" })
                  }
                  onRestoreGroup={(id) =>
                    groupStatusMutation.mutate({ id, status: "active" })
                  }
                  onArchiveOption={(id) =>
                    optionStatusMutation.mutate({ id, status: "archived" })
                  }
                  onRestoreOption={(id) =>
                    optionStatusMutation.mutate({ id, status: "active" })
                  }
                  isAdmin={isAdmin}
                />
              );
            })}
        </div>
      )}

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "Edit Tag Group" : "Create Tag Group"}
            </DialogTitle>
            <DialogDescription>
              {editingGroup
                ? "Update tag group details"
                : "Create a structured tag group"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g. Feeling Type, Mood Type, Shift Labels"
                data-testid="input-group-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Describe what this tag group represents"
                rows={2}
                data-testid="input-group-description"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={groupApprovalRequired}
                onCheckedChange={(checked) =>
                  setGroupApprovalRequired(!!checked)
                }
                data-testid="checkbox-approval-required"
              />
              <span className="text-sm">Require approval for new options</span>
            </label>

            <LanguageVariantsSection
              variants={groupVariants}
              activeVariantIndex={activeGroupVariantIndex}
              onActiveVariantChange={setActiveGroupVariantIndex}
              availableLanguages={availableGroupLanguages}
              onAddVariant={addGroupVariant}
              onSetDefault={setGroupAsDefault}
              pendingRemoveIndex={pendingGroupRemoveIndex}
              onRequestRemove={setPendingGroupRemoveIndex}
              onConfirmRemove={confirmRemoveGroupVariant}
              onCancelRemove={() => setPendingGroupRemoveIndex(null)}
            >
              {groupVariants[activeGroupVariantIndex] && (
                <div
                  className="space-y-4 pt-2"
                  data-testid="group-variant-fields"
                >
                  <div className="space-y-2">
                    <Label>Localized Title</Label>
                    <Input
                      value={
                        groupVariants[activeGroupVariantIndex].localizedTitle
                      }
                      onChange={(e) =>
                        updateGroupVariantField(
                          "localizedTitle",
                          e.target.value,
                        )
                      }
                      placeholder="Translated title"
                      data-testid={`input-group-variant-title-${groupVariants[activeGroupVariantIndex].languageCode}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={
                        groupVariants[activeGroupVariantIndex]
                          .localizedDescription
                      }
                      onChange={(e) =>
                        updateGroupVariantField(
                          "localizedDescription",
                          e.target.value,
                        )
                      }
                      placeholder="Translated description"
                      rows={2}
                      data-testid={`input-group-variant-description-${groupVariants[activeGroupVariantIndex].languageCode}`}
                    />
                  </div>
                </div>
              )}
            </LanguageVariantsSection>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={closeGroupDialog}
                data-testid="button-cancel-group"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveGroup}
                disabled={
                  !groupName.trim() ||
                  createGroupMutation.isPending ||
                  updateGroupMutation.isPending ||
                  saveGroupVariantsMutation.isPending
                }
                data-testid="button-save-group"
              >
                {(createGroupMutation.isPending ||
                  updateGroupMutation.isPending ||
                  saveGroupVariantsMutation.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingGroup ? "Save Changes" : "Create Tag Group"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={optionDialogOpen} onOpenChange={setOptionDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOption ? "Edit Tag Option" : "Add Tag Option"}
            </DialogTitle>
            <DialogDescription>
              {editingOption
                ? "Update this tag option"
                : "Add a new option to the tag group"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={optionLabel}
                onChange={(e) => setOptionLabel(e.target.value)}
                placeholder="e.g. anxious, overwhelmed, calm"
                data-testid="input-option-label"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={optionDescription}
                onChange={(e) => setOptionDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
                data-testid="input-option-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input
                type="number"
                value={optionDisplayOrder}
                onChange={(e) => setOptionDisplayOrder(Number(e.target.value))}
                min={0}
                data-testid="input-option-order"
              />
            </div>

            {isFeelingGroup && optionParentTagOptionIds.length > 0 && (
              <div className="space-y-2">
                <Label>Parent Mood</Label>
                <div className="flex flex-wrap gap-1.5">
                  {optionParentTagOptionIds.map((parentId) => {
                    const parentMood = moodOptions.find(
                      (m) => m.id === parentId,
                    );
                    return parentMood ? (
                      <Badge
                        key={parentId}
                        variant="secondary"
                        className="no-default-hover-elevate no-default-active-elevate"
                        data-testid={`parent-mood-badge-${parentId}`}
                      >
                        <Lock className="h-3 w-3 mr-1" />
                        {parentMood.label}
                      </Badge>
                    ) : null;
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  The parent mood is set when the feeling is created and cannot
                  be changed.
                </p>
              </div>
            )}

            <LanguageVariantsSection
              variants={optionVariants}
              activeVariantIndex={activeOptionVariantIndex}
              onActiveVariantChange={setActiveOptionVariantIndex}
              availableLanguages={availableOptionLanguages}
              onAddVariant={addOptionVariant}
              onSetDefault={setOptionAsDefault}
              pendingRemoveIndex={pendingOptionRemoveIndex}
              onRequestRemove={setPendingOptionRemoveIndex}
              onConfirmRemove={confirmRemoveOptionVariant}
              onCancelRemove={() => setPendingOptionRemoveIndex(null)}
            >
              {optionVariants[activeOptionVariantIndex] && (
                <div
                  className="space-y-4 pt-2"
                  data-testid="option-variant-fields"
                >
                  <div className="space-y-2">
                    <Label>Localized Label</Label>
                    <Input
                      value={
                        optionVariants[activeOptionVariantIndex].localizedLabel
                      }
                      onChange={(e) =>
                        updateOptionVariantField(
                          "localizedLabel",
                          e.target.value,
                        )
                      }
                      placeholder="Translated label"
                      data-testid={`input-option-variant-label-${optionVariants[activeOptionVariantIndex].languageCode}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={
                        optionVariants[activeOptionVariantIndex]
                          .localizedDescription
                      }
                      onChange={(e) =>
                        updateOptionVariantField(
                          "localizedDescription",
                          e.target.value,
                        )
                      }
                      placeholder="Translated description"
                      rows={2}
                      data-testid={`input-option-variant-description-${optionVariants[activeOptionVariantIndex].languageCode}`}
                    />
                  </div>
                </div>
              )}
            </LanguageVariantsSection>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={closeOptionDialog}
                data-testid="button-cancel-option"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveOption}
                disabled={
                  !optionLabel.trim() ||
                  createOptionMutation.isPending ||
                  updateOptionMutation.isPending ||
                  saveOptionVariantsMutation.isPending
                }
                data-testid="button-save-option"
              >
                {(createOptionMutation.isPending ||
                  updateOptionMutation.isPending ||
                  saveOptionVariantsMutation.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingOption ? "Save Changes" : "Add Option"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
